import { useLayoutEffect, useMemo, useState } from 'react'

import ProgramVolumeConfigs from 'components/Program/ProgramVolumeConfigs'
import MesoVolume from 'components/MesoVolume/MesoVolume'

const INITIAL_MOUNT_Y_OFFSET = 25
const MESO_VOLUME_CARD_WIDTH = 320
const MESO_VOLUME_CARD_HEIGHT = 350
const MESO_VOLUME_CARD_GAP = 24
const MESO_VOLUME_ROW_GAP = 96
const MACRO_VOLUME_CONTAINER_WIDTH = 1440
const MACRO_VOLUME_CONTAINER_PADDING = 24
const MACRO_VOLUME_CONTAINER_HEADER_HEIGHT = 24
const MACRO_VOLUME_PHASE_CHART_HEIGHT = 104
const MACRO_VOLUME_ITEM_TITLE_HEIGHT = 20
const MACRO_VOLUME_ITEM_INNER_GAP = 8
const MACRO_VOLUME_ITEM_ESTIMATED_HEIGHT = (
    MACRO_VOLUME_ITEM_TITLE_HEIGHT
    + MACRO_VOLUME_ITEM_INNER_GAP
    + MACRO_VOLUME_PHASE_CHART_HEIGHT
)
const MACRO_VOLUME_ITEM_GAP = 12
const MACRO_VOLUME_SECTION_GAP = 24

function groupMesoVolumeConfigsByFocusType(mesoVolumeConfigs) {
    const rows = []
    let currentFocusTypeId = undefined
    let currentRow = null

    mesoVolumeConfigs.forEach((mesoVolumeConfig) => {
        const focusTypeId = mesoVolumeConfig.macroVolumeFocusTypeId

        if (currentRow === null || focusTypeId !== currentFocusTypeId) {
            currentFocusTypeId = focusTypeId
            currentRow = []
            rows.push(currentRow)
        }

        currentRow.push(mesoVolumeConfig)
    })

    return rows
}

function estimateMacroVolumeContainerHeight(macroVolumeConfigs) {
    const headerBlockHeight = MACRO_VOLUME_CONTAINER_HEADER_HEIGHT + MACRO_VOLUME_ITEM_GAP

    if (macroVolumeConfigs.length === 0) {
        return MACRO_VOLUME_CONTAINER_PADDING + headerBlockHeight + 64
    }

    return MACRO_VOLUME_CONTAINER_PADDING
        + headerBlockHeight
        + (macroVolumeConfigs.length * MACRO_VOLUME_ITEM_ESTIMATED_HEIGHT)
        + (Math.max(0, macroVolumeConfigs.length - 1) * MACRO_VOLUME_ITEM_GAP)
}

function buildVolumeViewLayout(macroVolumeConfigs, mesoVolumeConfigs) {
    const mesoVolumeLayouts = []
    let offsetY = 0
    let mesoVolumeBoardWidth = 0

    const mesoVolumeRows = groupMesoVolumeConfigsByFocusType(mesoVolumeConfigs)

    mesoVolumeRows.forEach((mesoVolumeRow) => {
        let offsetX = 0

        mesoVolumeRow.forEach((mesoVolumeConfig) => {
            mesoVolumeLayouts.push({
                mesoVolumeConfig,
                x: offsetX,
                y: offsetY,
                width: MESO_VOLUME_CARD_WIDTH,
                height: MESO_VOLUME_CARD_HEIGHT,
            })

            offsetX += MESO_VOLUME_CARD_WIDTH + MESO_VOLUME_CARD_GAP
        })

        mesoVolumeBoardWidth = Math.max(
            mesoVolumeBoardWidth,
            Math.max(0, offsetX - MESO_VOLUME_CARD_GAP),
        )
        offsetY += MESO_VOLUME_CARD_HEIGHT + MESO_VOLUME_ROW_GAP
    })

    const mesoVolumeBoardHeight = Math.max(0, offsetY - MESO_VOLUME_ROW_GAP)
    const macroVolumeContainerHeight = estimateMacroVolumeContainerHeight(macroVolumeConfigs)

    return {
        mesoVolumeLayouts,
        mesoVolumeBoardWidth,
        mesoVolumeBoardHeight,
        macroVolumeContainerHeight,
        boardWidth: Math.max(MACRO_VOLUME_CONTAINER_WIDTH, mesoVolumeBoardWidth),
        boardHeight: macroVolumeContainerHeight
            + (mesoVolumeLayouts.length > 0 ? MACRO_VOLUME_SECTION_GAP + mesoVolumeBoardHeight : 0),
    }
}

export default function VolumeCanvasTab({
    viewportRef,
    setTransform = () => {},
    programMetadata = {},
    programVolumeConfigs = [],
    mesoVolumeConfigs = [],
}) {

    const [isCanvasReady, setIsCanvasReady] = useState(false)

    const volumeLayout = useMemo(
        () => buildVolumeViewLayout(programVolumeConfigs, mesoVolumeConfigs),
        [programVolumeConfigs, mesoVolumeConfigs],
    )

    const {
        mesoVolumeLayouts,
        mesoVolumeBoardWidth,
        mesoVolumeBoardHeight,
        macroVolumeContainerHeight,
        boardWidth,
        boardHeight,
    } = volumeLayout

    useLayoutEffect(() => {
        const viewport = viewportRef?.current
        if (!viewport) return

        const { width, height } = viewport.getBoundingClientRect()

        setTransform({
            x: Math.max(0, width / 2 - boardWidth / 2),
            y: Math.max(0, height / 2 - boardHeight / 2 + INITIAL_MOUNT_Y_OFFSET),
            zoom: 1,
        })

        setIsCanvasReady(true)
    }, [boardWidth, boardHeight, viewportRef, setTransform])

    useLayoutEffect(() => {
        return () => setIsCanvasReady(false)
    }, [])

    if (!isCanvasReady) {
        return null
    }

    const { macroVolumePhaseTypes, macroVolumePhaseTypeHashMap } = programMetadata

    return (
        <div className="absolute left-0 top-0">
            <div
                className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-md"
                style={{
                    width: MACRO_VOLUME_CONTAINER_WIDTH,
                    minHeight: macroVolumeContainerHeight,
                }}
                onPointerDown={(event) => event.stopPropagation()}
            >
                <p className="text-left text-base font-medium text-gray-900">
                    Program Phase Breakdowns
                </p>

                {programVolumeConfigs.length === 0 &&
                    <div className="py-6 text-center text-sm text-gray-400">
                        No program phase breakdowns
                    </div>
                }

                {programVolumeConfigs.map((macroVolumeConfig) => (
                    <div
                        key={macroVolumeConfig.programVolumeConfigId}
                        className="space-y-2"
                        style={{ minHeight: MACRO_VOLUME_ITEM_ESTIMATED_HEIGHT }}
                    >
                        <p className="text-left text-sm font-medium text-gray-900">
                            {macroVolumeConfig.name}
                        </p>

                        <ProgramVolumeConfigs
                            macroVolumePhases={macroVolumeConfig.macroVolumePhases ?? []}
                            macroVolumePhaseTypes={macroVolumePhaseTypes}
                            macroVolumePhaseTypeHashMap={macroVolumePhaseTypeHashMap}
                            programLengthLowerBound={macroVolumeConfig.programLengthLowerBound}
                            programLengthUpperBound={macroVolumeConfig.programLengthUpperBound}
                        />
                    </div>
                ))}
            </div>

            {mesoVolumeLayouts.length > 0 &&
                <div
                    className="relative"
                    style={{
                        marginTop: MACRO_VOLUME_SECTION_GAP,
                        width: Math.max(MACRO_VOLUME_CONTAINER_WIDTH, mesoVolumeBoardWidth),
                        height: mesoVolumeBoardHeight,
                    }}
                >
                    {mesoVolumeLayouts.map(({ mesoVolumeConfig, x, y, width }) => (
                        <div
                            key={mesoVolumeConfig.mesoVolumeId}
                            className="absolute rounded-lg bg-white"
                            style={{
                                left: x,
                                top: y,
                                width,
                                height: MESO_VOLUME_CARD_HEIGHT,
                            }}
                            onPointerDown={(event) => event.stopPropagation()}
                        >
                            <MesoVolume
                                programMetadata={programMetadata}
                                mesoVolume={mesoVolumeConfig}
                            />
                        </div>
                    ))}
                </div>
            }
        </div>
    )
}
